import { makeObservable, observable, action, computed } from 'mobx'
import axios from 'axios'
import { BaseStore } from './BaseStore'
import type { Room, Message } from '@core/models'
import type { RoomResponse } from '@core/index'

export class RoomStore extends BaseStore {
  rooms: Map<string, Room> = new Map()
  currentRoom: RoomResponse | null = null

  constructor() {
    super()
    makeObservable(this, {
      rooms: observable,
      currentRoom: observable,
      getRooms: action,
      getRoom: action,
      updateRoom: action,
      roomsArray: computed,
      currentRoomMessages: computed
    })
  }

  async getRooms(tenantId: string) {
    const result = await this.handleApiCall(async () => {
      const response = await axios.get<Room[]>(
        this.apiPath(`v1/tenants/${tenantId}/rooms`),
        { headers: this.generateFetchHeader() }
      )
      return response.data
    })

    if (result) {
      this.rooms.clear()
      result.forEach(room => {
        this.rooms.set(room.id, room)
      })
      return true
    }
    return false
  }

  async getRoom(tenantId: string, roomId: string) {
    const result = await this.handleApiCall(async () => {
      const response = await axios.get<RoomResponse>(
        this.apiPath(`v1/tenants/${tenantId}/rooms/${roomId}`),
        { headers: this.generateFetchHeader() }
      )
      return response.data
    })

    if (result) {
      this.currentRoom = result
      // roomsマップには基本情報のみを保存（messagesを除外）
      if (result.id) {
        const { messages, ...roomData } = result
        this.rooms.set(result.id, roomData as Room)
      }
      return true
    }
    return false
  }


  async updateRoom(tenantId: string, roomId: string, data: Partial<Room>) {
    const result = await this.handleApiCall(async () => {
      await axios.put(
        this.apiPath(`v1/tenants/${tenantId}/rooms/${roomId}`),
        data,
        { headers: this.generateFetchHeader() }
      )
      return true
    })

    if (result) {
      // 更新されたルーム情報を再取得
      await this.getRoom(tenantId, roomId)
      this.tryShowToast('ルーム情報を更新しました', 'success')
      return true
    }

    this.tryShowToast('ルーム情報の更新に失敗しました', 'error')
    return false
  }

  get roomsArray() {
    return Array.from(this.rooms.values())
  }

  get currentRoomMessages(): Message[] {
    return (this.currentRoom?.messages?.filter((m: Message | undefined): m is Message => m !== undefined) || [])
  }
}